import React from "react";
import FooterDrawer from "@/components/ui/atoms/FooterDrawer";
import {
  WheelPicker,
  WheelPickerWrapper,
  type WheelPickerOption,
} from "@/components/ui/atoms/MWheelPicker";
import styles from "../molecules/MDatePicker.module.css";

/**
 * Props for the MTimePicker component.
 *
 * @property open - Whether the picker drawer is open.
 * @property onClose - Callback when the drawer is closed.
 * @property onDone - Callback when a time is selected and confirmed.
 * @property initialValue - (Optional) Initial time value as a Date object. Defaults to current time if not provided.
 * @property title - (Optional) Title displayed in the picker drawer. Defaults to 'Select Time'.
 * @property format - (Optional) Time format: 12 for 12-hour (AM/PM), 24 for 24-hour. Defaults to 24 if not specified.
 * @property minuteStep - (Optional) Step size for minutes (e.g., 1, 5, 15, 30). When set to 15, only 0, 15, 30, 45 will be available.
 *
 * Usage: <MTimePicker open={open} onClose={...} onDone={...} format={12} minuteStep={15} />
 */
export interface MTimePickerProps {
  open: boolean;
  onClose: () => void;
  onDone: (date: Date) => void;
  initialValue?: Date;
  title?: string;
  format?: 12 | 24; // 12 or 24 hour mode
  /**
   * Step size for minutes (e.g., 1, 5, 15, 30)
   * When set to 15, only 0, 15, 30, 45 will be available
   */
  minuteStep?: number;
}

/**
 * MTimePicker is a modal time selection component with wheel pickers for hours and minutes.
 *
 * - Supports both 12-hour (AM/PM) and 24-hour formats (default is 24-hour).
 * - Opens in a FooterDrawer and calls onDone with a Date object when selection is confirmed.
 * - Use `format={12}` for 12-hour mode, omit or set `format={24}` for 24-hour mode.
 *
 * Example:
 * ```tsx
 * <MTimePicker open={open} onClose={...} onDone={...} format={12} />
 * ```
 */
export const parseTimeString = (
  value?: string,
  format: 12 | 24 = 24
): Date | undefined => {
  if (!value) return undefined;
  const now = new Date();
  if (format === 12) {
    // Expect format: hh:mm AM/PM
    const match = value.match(/^(\d{1,2}):(\d{2}) ?(AM|PM)$/i);
    if (match) {
      let hour = parseInt(match[1], 10);
      const minute = parseInt(match[2], 10);
      const ampm = match[3].toUpperCase();
      if (ampm === "PM" && hour < 12) hour += 12;
      if (ampm === "AM" && hour === 12) hour = 0;
      now.setHours(hour, minute, 0, 0);
      return now;
    }
  } else {
    // Expect format: HH:mm
    const match = value.match(/^(\d{1,2}):(\d{2})$/);
    if (match) {
      const hour = parseInt(match[1], 10);
      const minute = parseInt(match[2], 10);
      now.setHours(hour, minute, 0, 0);
      return now;
    }
  }
  return undefined;
};

export const formatTimeString = (date: Date, format: 12 | 24 = 24): string => {
  const hours = date.getHours();
  const minutes = date.getMinutes();
  if (format === 12) {
    let hour12 = hours % 12;
    if (hour12 === 0) hour12 = 12;
    const ampm = hours >= 12 ? "PM" : "AM";
    return `${hour12.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")} ${ampm}`;
  } else {
    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}`;
  }
};

const MTimePicker: React.FC<MTimePickerProps> = ({
  open,
  onClose,
  onDone,
  initialValue,
  title = "Select Time",
  format = 24,
  minuteStep = 1,
}) => {
  // Always use current time if no initialValue
  const now = new Date();
  const initial = initialValue || now;
  const timeFormat = format === 12 ? 12 : 24;

  // 12-hour format state
  const [selectedHour12, setSelectedHour12] = React.useState(() => {
    let h = initial.getHours();
    if (timeFormat === 12) {
      h = h % 12;
      if (h === 0) h = 12;
      return h;
    }
    return initial.getHours();
  });
  // Must round to a valid minuteStep value here too, not just in the
  // initialValue-sync effect below — when there's no initialValue (initial
  // falls back to `now`), the raw current minute almost never lands on a
  // step boundary (e.g. minuteStep=15 but now.getMinutes() is 37). The
  // WheelPicker then can't find that value among its stepped options and
  // silently fails to sync (library logs an error and bails), leaving the
  // minute wheel stuck unrotated with no neighboring values visible.
  const [selectedMinute, setSelectedMinute] = React.useState(() => {
    const mins = initial.getMinutes();
    const rounded = Math.round(mins / minuteStep) * minuteStep;
    return rounded >= 60 ? 0 : rounded;
  });
  const [selectedAmPm, setSelectedAmPm] = React.useState(() => {
    if (timeFormat === 12) {
      return initial.getHours() >= 12 ? "PM" : "AM";
    }
    return "AM";
  });
  // 24-hour format state
  const [selectedHour24, setSelectedHour24] = React.useState(
    initial.getHours()
  );

  // Sync selected values with initialValue when picker is opened
  React.useEffect(() => {
    if (open && initialValue) {
      const h = initialValue.getHours();

      // Round minutes to nearest step
      const mins = initialValue.getMinutes();
      const roundedMinutes = Math.round(mins / minuteStep) * minuteStep;
      setSelectedMinute(roundedMinutes >= 60 ? 0 : roundedMinutes);

      if (timeFormat === 12) {
        let h12 = h % 12;
        if (h12 === 0) h12 = 12;
        setSelectedHour12(h12);
        setSelectedAmPm(h >= 12 ? "PM" : "AM");
      } else {
        setSelectedHour24(h);
      }
    }
  }, [open, initialValue, timeFormat, minuteStep]);

  // --- Wheels ---
  const hours12 = Array.from({ length: 12 }, (_, i) => i + 1); // 1-12
  const hours24 = Array.from({ length: 24 }, (_, i) => i); // 0-23

  // Generate minutes based on minuteStep
  const minutes = Array.from(
    { length: Math.floor(60 / minuteStep) },
    (_, i) => i * minuteStep
  );
  // --- Handlers ---
  const handleHourChange = (idx: number) => {
    if (timeFormat === 12) setSelectedHour12(hours12[idx]);
    else setSelectedHour24(hours24[idx]);
  };
  const handleMinuteChange = (idx: number) => setSelectedMinute(minutes[idx]);
  const handleAmPmChange = (idx: number) =>
    setSelectedAmPm(idx === 0 ? "AM" : "PM");
  const handleDone = () => {
    const d = new Date();
    let hour = 0;
    if (timeFormat === 12) {
      hour = selectedHour12 % 12;
      if (selectedAmPm === "PM") hour += 12;
    } else {
      hour = selectedHour24;
    }
    // Ensure minute is one of the valid step values
    const validMinute = Math.round(selectedMinute / minuteStep) * minuteStep;
    d.setHours(hour, validMinute, 0, 0);
    onDone(d);
  };
  // No need to calculate indexes as we're using the values directly in the WheelPicker components
  // --- WheelPicker-based rendering ---
  // Prepare options for wheels
  const hourOptions12: WheelPickerOption[] = hours12.map((h) => ({
    label: h.toString().padStart(2, "0"),
    value: h.toString(),
  }));
  const hourOptions24: WheelPickerOption[] = hours24.map((h) => ({
    label: h.toString().padStart(2, "0"),
    value: h.toString(),
  }));
  const minuteOptions: WheelPickerOption[] = minutes.map((m) => ({
    label: m.toString().padStart(2, "0"),
    value: m.toString(),
  }));
  const ampmOptions: WheelPickerOption[] = [
    { label: "AM", value: "AM" },
    { label: "PM", value: "PM" },
  ];

  return (
    <FooterDrawer
      open={open}
      onClose={onClose}
      title={title}
      showClose={false}
      height="313px"
      donePosition="top"
      onDone={handleDone}
      containerZIndex={9999999999}
      doneText="Done"
    >
      <div className={styles.pickerWrapTime}>
        <WheelPickerWrapper>
          <WheelPicker
            options={timeFormat === 12 ? hourOptions12 : hourOptions24}
            value={
              timeFormat === 12
                ? selectedHour12.toString()
                : selectedHour24.toString()
            }
            onValueChange={(val) =>
              handleHourChange(
                (timeFormat === 12 ? hourOptions12 : hourOptions24).findIndex(
                  (opt) => opt.value === val
                )
              )
            }
            infinite
          />
          <WheelPicker
            options={minuteOptions}
            value={selectedMinute.toString()}
            onValueChange={(val) =>
              handleMinuteChange(
                minuteOptions.findIndex((opt) => opt.value === val)
              )
            }
            infinite
          />
          {timeFormat === 12 && (
            <WheelPicker
              options={ampmOptions}
              value={selectedAmPm}
              onValueChange={(val) =>
                handleAmPmChange(
                  ampmOptions.findIndex((opt) => opt.value === val)
                )
              }
            />
          )}
        </WheelPickerWrapper>
      </div>
    </FooterDrawer>
  );
};

export default MTimePicker;
